"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Ellipsis, House, X } from "lucide-react";

import { COMMON, navLabel } from "../_lib/copy-shared";
import type { SupplyLang } from "../_lib/lang";
import { SUPPLY_MODULES, type SupplyNavKey } from "../_lib/nav";
import { withOutlet } from "../_lib/with-outlet";

/** Keys that already sit on the bar — the sheet lists everything else. */
export const BAR_KEYS: ReadonlySet<SupplyNavKey> = new Set(["order", "receive", "count", "waste"]);

/**
 * A1 phone "More" — a bottom sheet over the non-scrolling shell (absolute,
 * never fixed; SupplyShell rule). Markup always renders every row; the same
 * pre-paint CSS gates (`supply-nav-<key>`, `supply-tab-approvals`) decide
 * visibility, so a skeleton and a page render identical markup.
 * Client component: open/close is the only state.
 *
 * The sheet PORTALS into `.supply-shell` rather than rendering where the
 * button sits. Nested inside the tab bar's own <nav> — which is itself
 * `absolute` — the bar becomes the containing block, so `inset: 0` sized the
 * backdrop to the ~70px bar strip: no dimming over the page and tapping
 * outside the sheet did nothing (caught in the live width sweep, invisible to
 * jsdom). The shell is the `relative` box the bar and rail already anchor to,
 * so the portal keeps the absolute-never-fixed rule intact and simply gives
 * both layers the right containing block. Portal target is resolved lazily in
 * the open click (never a mount effect); closed renders nothing either way, so
 * skeleton/page markup parity is unaffected.
 *
 * `lang` (REQUIRED, ../_lib/lang.ts) resolves each row's label via
 * NAV_COPY/navLabel (../_lib/copy-shared.ts) — passed down from
 * SupplyTabBar, itself passed down from the page's own `await
 * getSupplyLang()`, never read from a cookie in this client component. The
 * group headers (`mod.label`) are keyed by SupplyModuleId, a different type
 * NAV_COPY does not cover, so they stay English — out of this task's scope.
 */
export function SupplyMoreSheet({
  active,
  outletCode,
  lang,
}: {
  active: SupplyNavKey | null;
  outletCode: string | null;
  /** REQUIRED — see ../_lib/lang.ts. */
  lang: SupplyLang;
}) {
  const [open, setOpen] = useState(false);
  const [shell, setShell] = useState<Element | null>(null);
  const isActive = active !== null && !BAR_KEYS.has(active);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <>
      <button
        type="button"
        aria-label={COMMON.moreSectionsAria[lang]}
        aria-expanded={open}
        onClick={() => {
          setShell(document.querySelector(".supply-shell"));
          setOpen(true);
        }}
        className="supply-tab-link flex min-h-[56px] w-full items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--amber)]"
      >
        <span
          className={[
            "supply-tab-pill flex min-w-0 flex-col items-center justify-center gap-[3px] rounded-[12px] py-1.5",
            isActive
              ? "bg-[var(--amber-well)] font-bold text-[var(--amber)]"
              : "font-semibold text-[var(--muted)]",
          ].join(" ")}
        >
          <Ellipsis className="size-[22px]" strokeWidth={2.1} aria-hidden />
          <span className="supply-tab-label">{COMMON.more[lang]}</span>
        </span>
      </button>
      {open && shell
        ? createPortal(
        <>
          <div className="supply-more-backdrop" onClick={() => setOpen(false)} aria-hidden />
          <div role="dialog" aria-modal="true" aria-label={COMMON.allSectionsAria[lang]} className="supply-more-sheet">
            <div className="mb-2 flex items-center justify-between px-1">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[var(--muted)]">
                {COMMON.allSectionsTitle[lang]}
              </span>
              <button
                type="button"
                aria-label={COMMON.closeAria[lang]}
                onClick={() => setOpen(false)}
                className="flex size-[34px] items-center justify-center rounded-[12px] bg-[var(--raised)] text-[var(--muted)]"
              >
                <X className="size-[18px]" strokeWidth={2.3} aria-hidden />
              </button>
            </div>
            {/* Home sits above the module groups, ungated: /supply is
                requireSupplyReader (every session that can open this sheet can
                open home), and on phone this row plus the header lockup are
                the only ways back to the dashboard. */}
            <ul className="mb-3 flex flex-col gap-[2px]">
              <li>
                <Link
                  href={withOutlet("/supply", outletCode)}
                  prefetch={false}
                  onClick={() => setOpen(false)}
                  className="flex min-h-[48px] items-center gap-3 rounded-[12px] px-2 text-[14px] font-semibold text-[var(--body)]"
                >
                  <House className="size-[18px] shrink-0" strokeWidth={2.1} aria-hidden />
                  {COMMON.home[lang]}
                </Link>
              </li>
            </ul>
            {/* These wrappers deliberately reuse the rail's supply-mod-* flag
                gates, so an all-flags-off module is also hidden in the sheet.
                Every supply_* flag is on in prod as checked 2026-08-12; the
                landing-surface mismatch remains latent while that stays true. */}
            {SUPPLY_MODULES.map((mod) => {
              const rows = mod.items.filter((i) => !BAR_KEYS.has(i.key));
              if (rows.length === 0) return null;
              return (
                <div key={mod.id} className={`supply-mod-${mod.id} mb-3`}>
                  <p className="px-1 pb-1 text-[11px] font-extrabold uppercase tracking-[0.09em] text-[var(--muted)]">
                    {mod.label}
                  </p>
                  <ul className="flex flex-col gap-[2px]">
                    {rows.map((item) => {
                      const Icon = item.icon;
                      const gateClass =
                        item.key === "approvals" ? "supply-tab-approvals supply-nav-approvals" : `supply-nav-${item.key}`;
                      return (
                        <li key={item.key}>
                          <Link
                            href={withOutlet(item.href, outletCode)}
                            prefetch={false}
                            onClick={() => setOpen(false)}
                            className={`${gateClass} flex min-h-[48px] items-center gap-3 rounded-[12px] px-2 text-[14px] font-semibold text-[var(--body)]`}
                          >
                            <Icon className="size-[18px] shrink-0" strokeWidth={2.1} aria-hidden />
                            {navLabel(item.key, lang)}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>
        </>,
            shell,
          )
        : null}
    </>
  );
}
